(() => {
  if (customElements.get('ss-store-comparison')) return;
  const key = 'sejlstroem:comparison:v1';
  const cleanHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value);
  const validPrice = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;
  const el = (tag, text, cls) => {
    const node = document.createElement(tag);
    if (text != null) node.textContent = text;
    if (cls) node.className = cls;
    return node;
  };
  class StoreComparison extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.labels = JSON.parse(this.querySelector('[data-compare-labels]').textContent);
      this.dialog = this.querySelector('dialog');
      this.status = this.querySelector('[data-compare-status]');
      this.table = this.querySelector('[data-compare-table]');
      this.items = [];
      this.products = new Map();
      this.root = this.dataset.root.replace(/\/?$/, '/');
      try {
        const saved = JSON.parse(sessionStorage.getItem(key) || '[]');
        if (Array.isArray(saved)) this.items = saved.filter(x => x && cleanHandle(x.handle)).filter((x,i,a) => a.findIndex(y => y.handle === x.handle) === i).slice(0,4).map(x => ({handle:x.handle, variant:String(x.variant || ''), variantChanged:x.variantChanged === true}));
      } catch (_) {}
      document.addEventListener('ss:compare-toggle', event => this.toggle(event.detail));
      this.querySelector('[data-compare-open]').addEventListener('click', () => this.open());
      this.querySelector('[data-compare-close]').addEventListener('click', () => this.dialog.close());
      this.querySelector('[data-compare-clear]').addEventListener('click', () => { this.items=[]; this.sync(); });
      this.querySelector('[data-compare-retry]').addEventListener('click', () => this.refresh());
      this.querySelector('[data-compare-differences]').addEventListener('change', () => this.render());
      this.dialog.addEventListener('close', () => { this.controller?.abort(); this.opener?.focus(); });
      this.sync();
    }
    sync() {
      try { sessionStorage.setItem(key, JSON.stringify(this.items)); } catch (_) {}
      this.querySelector('.ss-compare-tray').hidden = !this.items.length;
      this.querySelector('[data-compare-count]').textContent = `${this.items.length}/4`;
      document.dispatchEvent(new CustomEvent('ss:compare-state'));
    }
    toggle(item) {
      if (!item || !cleanHandle(item.handle)) return;
      const index = this.items.findIndex(x => x.handle === item.handle);
      if (index !== -1) this.items.splice(index,1);
      else if (this.items.length < 4) this.items.push({handle:item.handle,variant:String(item.variant || '')});
      else { this.querySelector('[data-compare-announcement]').textContent=this.labels.limit; this.open(); return; }
      this.sync();
      this.querySelector('[data-compare-announcement]').textContent = `${this.labels.selected}: ${this.items.length}/4`;
    }
    open() {
      this.opener = document.activeElement;
      if (!this.dialog.open) this.dialog.showModal();
      this.refresh();
    }
    async refresh() {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const timer = setTimeout(() => controller.abort(), 15000);
      this.products.clear();
      this.table.replaceChildren();
      this.status.textContent = this.labels.loading;
      const categoryNote = this.querySelector('[data-compare-category-note]');
      if (categoryNote) { categoryNote.hidden = true; categoryNote.textContent = ''; }
      this.querySelector('[data-compare-retry]').hidden = true;
      this.table.setAttribute('aria-busy','true');
      try {
        const products = await Promise.allSettled(this.items.map(async item => {
          const response = await fetch(`${this.root}products/${encodeURIComponent(item.handle)}.js`, {credentials:'same-origin',cache:'no-store',signal:controller.signal});
          if (!response.ok) throw new Error('product');
          const product = await response.json();
          if (!Array.isArray(product.variants) || !product.variants.length || product.handle !== item.handle) throw new Error('data');
          return product;
        }));
        if (this.controller !== controller || !this.dialog.open) return;
        products.forEach(result => { if (result.status === 'fulfilled') this.products.set(result.value.handle,result.value); });
        this.render();
      } catch (_) {
        if (this.controller !== controller || !this.dialog.open) return;
        this.status.textContent = this.labels.error;
        this.querySelector('[data-compare-retry]').hidden = false;
      } finally {
        clearTimeout(timer);
        if (this.controller === controller) this.table.removeAttribute('aria-busy');
      }
    }
    productInfo(product) {
      const doc = new DOMParser().parseFromString(product.description || '', 'text/html');
      doc.querySelectorAll('script,style,iframe,form,button').forEach(n => n.remove());
      const specs = new Map();
      doc.querySelectorAll('table tr').forEach(row => {
        const cells = Array.from(row.children).filter(x => /^(TD|TH)$/.test(x.tagName));
        if (cells.length !== 2) return;
        const label = cells[0].textContent.trim().replace(/\s+/g,' ');
        const value = cells[1].textContent.trim().replace(/\s+/g,' ');
        if (label.length > 1 && label.length < 81 && value.length && value.length < 500) {
          const key = label.toLocaleLowerCase().replace(/[:\s]+$/,'');
          const entry = specs.get(key);
          if (!entry) specs.set(key,{label,values:[value]});
          else if (!entry.values.includes(value)) entry.values.push(value);
        }
      });
      const paragraphs = Array.from(doc.querySelectorAll('p')).map(p => p.textContent.trim().replace(/\s+/g,' ')).filter(s => s.length > 40);
      let description = paragraphs.slice(0,2).join(' ');
      if (description.length > 600) description = description.slice(0,597).replace(/\s+\S*$/,'')+'…';
      return {specs,description};
    }
    money(cents) {
      if (!validPrice(cents)) return this.labels.price_missing;
      return new Intl.NumberFormat(this.dataset.locale, {style:'currency',currency:this.dataset.currency}).format(cents/100);
    }
    render() {
      const t=this.labels;
      const columns=this.items.map(item => {
        const product=this.products.get(item.handle);
        if (!product) return {item,product:null,variant:null,specs:new Map(),description:''};
        const selectedVariant=product.variants.find(v => String(v.id) === item.variant);
        const variant=selectedVariant || product.variants.find(v => v.available) || product.variants[0];
        if (item.variant && !selectedVariant) item.variantChanged=true;
        item.variant=String(variant.id);
        return {item,product,variant,...this.productInfo(product)};
      });
      this.table.replaceChildren();
      const failed=columns.filter(c => !c.product).length;
      this.status.textContent=failed ? (failed === columns.length ? t.error : t.partial_error) : columns.length === 0 ? t.empty : columns.length === 1 ? t.single : '';
      this.querySelector('[data-compare-retry]').hidden=!columns.length;
      const types=new Set(columns.map(c => c.product?.type?.trim().toLocaleLowerCase()).filter(Boolean));
      const categoryNote=this.querySelector('[data-compare-category-note]');
      if (categoryNote) { categoryNote.hidden=types.size<2; categoryNote.textContent=types.size>1?t.mixed_types:''; }
      if (!columns.length) return;
      const table=el('table');
      table.className='ss-compare-table';
      const caption=el('caption',t.specifications,'visually-hidden');
      table.append(caption);
      const head=el('thead'); const tr=el('tr');
      tr.append(el('td'));
      columns.forEach(({item,product,variant}) => {
        const th=el('th'); th.scope='col';
        if (product) {
          const source=variant.featured_image?.src || product.featured_image;
          let imageAdded=false;
          if (source) {
            try {
              const url=new URL(source,location.origin);
              if (url.protocol === 'https:') {
                const image=el('img');image.src=url.href;image.alt=product.title;image.width=200;image.height=140;image.loading='lazy';th.append(image);imageAdded=true;
              }
            } catch (_) {}
          }
          if (!imageAdded) th.append(el('span',t.image_missing,'ss-compare-image-missing'));
          th.append(el('h3',product.title));
          if (item.variantChanged) th.append(el('p',t.variant_changed,'ss-compare-field-note'));
          const select=el('select'); select.setAttribute('aria-label',`${t.variant}: ${product.title}`); select.dataset.compareVariant=item.handle;
          product.variants.forEach(v => {
            const option=el('option',`${v.title === 'Default Title' ? t.variant : v.title}${v.available ? '' : ' · '+t.unavailable}`);
            option.value=String(v.id);option.selected=String(v.id) === String(variant.id);select.append(option);
          });
          select.addEventListener('change', () => {item.variant=select.value;item.variantChanged=false;this.sync();this.render();this.table.querySelector(`[data-compare-variant="${item.handle}"]`)?.focus();});
          th.append(select);
          const view=el('a',t.view,'ss-compare-view');view.href=`${this.root}products/${encodeURIComponent(item.handle)}?variant=${variant.id}`;th.append(view);
        } else {
          th.append(el('h3',item.handle));
          th.append(el('p',t.product_error,'ss-compare-field-note'));
        }
        const remove=el('button',t.remove,'ss-compare-remove');remove.type='button';remove.setAttribute('aria-label',`${t.remove}: ${product?.title || item.handle}`);
        remove.addEventListener('click', () => {this.items=this.items.filter(x => x.handle!==item.handle);this.sync();this.render();this.querySelector('[data-compare-close]').focus();});th.append(remove);
        tr.append(th);
      });
      head.append(tr);table.append(head);
      const body=el('tbody');
      const differences=this.querySelector('[data-compare-differences]').checked;
      const row=(label,values,always=false,cls='') => {
        if (differences && columns.length>1 && !always && new Set(values).size<2) return;
        const tr=el('tr',null,cls);const th=el('th',label);th.scope='row';tr.append(th);
        values.forEach(v => tr.append(el('td',v || t.missing)));body.append(tr);
      };
      row(t.price,columns.map(c => this.money(c.variant?.price)),true);
      if (columns.length>1 && columns.every(c => validPrice(c.variant?.price))) {
        const lowest=Math.min(...columns.map(c => c.variant.price));
        row(t.price_difference,columns.map(c => c.variant.price === lowest ? t.lowest_price : '+'+this.money(c.variant.price-lowest)),true,'ss-compare-price-difference');
      }
      row(t.availability,columns.map(c => c.variant ? (c.variant.available?t.available:t.unavailable) : t.missing),true);
      row(t.sku,columns.map(c => c.variant?.sku));
      const options=new Set(columns.flatMap(c => (c.product?.options || []).map(o => typeof o==='string'?o:o.name).filter(n => n && n!=='Title')));
      options.forEach(name => row(name,columns.map(c => { const i=(c.product?.options || []).findIndex(o => (typeof o==='string'?o:o.name)===name);return i<0?'':c.variant?.options?.[i]; })));
      const group=el('tr',null,'ss-compare-specs-heading');
      const groupCell=el('td');groupCell.colSpan=columns.length+1;
      groupCell.append(el('strong',t.specs_heading),el('p',t.specs_note,'ss-compare-field-note'));
      group.append(groupCell);body.append(group);
      row(t.type,columns.map(c => c.product?.type));
      const specs=new Map();columns.forEach(c => c.specs.forEach((value,key) => {if(!specs.has(key)) specs.set(key,value.label);}));
      Array.from(specs).slice(0,24).forEach(([key,label]) => row(label,columns.map(c => c.specs.get(key)?.values.join(' · ') || t.missing)));
      row(t.description,columns.map(c => c.description));
      table.append(body);this.table.append(table);
      if (specs.size>24) this.table.append(el('p',t.specs_truncated,'ss-compare-field-note'));
      this.sync();
    }
  }
  class CompareButton extends HTMLElement {
    connectedCallback() {
      this.button=this.querySelector('button');
      this.onClick ||= event => {event.preventDefault();event.stopPropagation();this.dispatchEvent(new CustomEvent('ss:compare-toggle',{bubbles:true,detail:{handle:this.dataset.handle,variant:this.dataset.variant}}));};
      this.onState ||= () => {
        const store=document.querySelector('ss-store-comparison');
        const selected=!!store?.items?.some(x => x.handle===this.dataset.handle);
        this.button.setAttribute('aria-pressed',String(selected));
        if(store?.labels)this.querySelector('[data-compare-label]').textContent=selected?store.labels.selected:store.labels.add;
      };
      this.button.addEventListener('click',this.onClick);
      document.addEventListener('ss:compare-state',this.onState);
      this.onState();
    }
    disconnectedCallback() {this.button?.removeEventListener('click',this.onClick);document.removeEventListener('ss:compare-state',this.onState);}
  }
  customElements.define('ss-store-comparison',StoreComparison);
  customElements.define('ss-compare-button',CompareButton);
})();
